/** PDF/프린트 메타(제목) 영역 — 본문 표 대비 시각적 강조 */
export const DOCUMENT_EXPORT_META_RENDER_SCALE = 1;

/** 주간학습 안내 PDF·프린트 제목 출력 확대 (캡처 높이 보정) */
export const WEEKLY_GUIDE_META_RENDER_SCALE = 1.5;

export const WEEKLY_GUIDE_META_TITLE_FONT_PX = 40;

const META_TITLE_FONT_RULES: Array<{ selector: string; fontSizePx: number }> = [
  { selector: '.cp-weekly-learning-guide-export-title-line1', fontSizePx: WEEKLY_GUIDE_META_TITLE_FONT_PX },
  { selector: '.cp-annual-operation-plan-export-title', fontSizePx: 28 },
  { selector: '.cp-annual-operation-plan-subtitle', fontSizePx: 22 },
  { selector: '.cp-annual-cross-topic-detail-subtitle', fontSizePx: 20 },
  { selector: '.cp-annual-cross-topic-detail-topic', fontSizePx: 20 },
  { selector: '.cp-annual-cross-topic-detail-scope', fontSizePx: 17 },
  { selector: '.cp-annual-cross-topic-program-plan-doc-title', fontSizePx: 20 },
  { selector: '.cp-annual-cross-topic-program-plan-doc-title-text', fontSizePx: 20 },
  { selector: '.cp-neis-learning-upload-export-subtitle', fontSizePx: 20 },
  { selector: '.cp-neis-learning-upload-export-title', fontSizePx: 20 },
  { selector: '.cp-grade-track-class-block-label', fontSizePx: 20 },
];

/** html2canvas 직전 클론 — 캡처 CSS·rem 계산과 무관하게 제목 크기 고정 */
export function applyExportMetaTitleFontBoost(metaClone: HTMLElement): void {
  for (const { selector, fontSizePx } of META_TITLE_FONT_RULES) {
    const nodes: HTMLElement[] = [];
    if (metaClone.matches(selector)) {
      nodes.push(metaClone);
    }
    metaClone.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLElement) nodes.push(node);
    });
    nodes.forEach((node) => {
      node.style.setProperty('font-size', `${fontSizePx}px`, 'important');
      node.style.setProperty('line-height', '1.35', 'important');
    });
  }
}

export function isWeeklyGuideExportMeta(metaEl: HTMLElement): boolean {
  return metaEl.classList.contains('cp-weekly-learning-guide-export-meta');
}

function measureMetaContentWidthPx(metaEl: HTMLElement): number {
  const rectWidth = Math.ceil(metaEl.getBoundingClientRect().width);
  return Math.ceil(Math.max(metaEl.scrollWidth, metaEl.offsetWidth, rectWidth));
}

/**
 * 제목 메타는 본문 표보다 좁은 경우가 많다. 표 전체 너비로만 캡처하면 PDF 제목 띠가 너무 낮고,
 * 너무 좁게 캡처하면 normalize 시 세로 확대가 과해진다.
 */
export function resolveExportMetaCaptureWidthPx(metaEl: HTMLElement, alignWidthPx?: number): number {
  const natural = Math.max(measureMetaContentWidthPx(metaEl), 360);

  if (isWeeklyGuideExportMeta(metaEl) && alignWidthPx) {
    const minW = Math.round(alignWidthPx * 0.55);
    const maxW = Math.round(alignWidthPx * 0.72);
    return Math.min(maxW, Math.max(minW, natural));
  }

  if (alignWidthPx && natural >= alignWidthPx * 0.85) {
    return alignWidthPx;
  }

  return natural;
}

export function resolveMetaNormalizeMaxUpscale(metaEl: HTMLElement | null): number {
  if (metaEl && isWeeklyGuideExportMeta(metaEl)) return 2.15;
  return 2.5;
}

export function prepareExportMetaCaptureClone(metaClone: HTMLElement, captureWidthPx: number): void {
  const isWeeklyGuide = isWeeklyGuideExportMeta(metaClone);
  metaClone.style.position = 'relative';
  metaClone.style.left = 'auto';
  metaClone.style.top = 'auto';
  metaClone.style.transform = 'none';
  metaClone.style.width = `${captureWidthPx}px`;
  metaClone.style.maxWidth = `${captureWidthPx}px`;
  metaClone.style.margin = '0 auto';
  metaClone.style.boxSizing = 'border-box';
  applyExportMetaTitleFontBoost(metaClone);
  if (isWeeklyGuide) {
    metaClone.style.display = 'flex';
    metaClone.style.flexDirection = 'column';
    metaClone.style.alignItems = 'center';
    metaClone.style.justifyContent = 'flex-start';
    metaClone.style.minHeight = 'auto';
    metaClone.style.padding = '12px 12px 10px';
    metaClone.querySelectorAll('.cp-weekly-learning-guide-export-title-line1').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.style.setProperty('white-space', 'normal', 'important');
      node.style.setProperty('word-break', 'keep-all', 'important');
    });
  }
}

export function buildDocumentExportMetaTitleCaptureCss(): string {
  return META_TITLE_FONT_RULES.map(
    ({ selector, fontSizePx }) => `
${selector} {
  font-size: ${fontSizePx}px !important;
  line-height: 1.35 !important;
}
`
  ).join('');
}

export function scaleCanvasUniform(source: HTMLCanvasElement, factor: number): HTMLCanvasElement {
  if (factor <= 1) return source;
  const scaled = document.createElement('canvas');
  scaled.width = Math.max(1, Math.round(source.width * factor));
  scaled.height = Math.max(1, Math.round(source.height * factor));
  const ctx = scaled.getContext('2d');
  if (!ctx) return source;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, scaled.width, scaled.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, scaled.width, scaled.height);
  return scaled;
}

export function boostMetaCanvasForRender(
  metaCanvas: HTMLCanvasElement | null,
  scale = DOCUMENT_EXPORT_META_RENDER_SCALE
): HTMLCanvasElement | null {
  if (!metaCanvas) return null;
  return scaleCanvasUniform(metaCanvas, scale);
}
